import React, { useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import { FormattedMessage } from 'react-intl';
import { DICOMPatientPresentation } from '~/backend/connector-api/connector-schemas';
import SensitiveInfoTypography from '~/components/SensitiveInfoTypography';
import { formatDateString } from '~/utils/date';

type PatientDetailsPanelProps = {
    patient: DICOMPatientPresentation | undefined;
};

const PatientDetailsPanel = React.memo(
    ({ patient }: PatientDetailsPanelProps) => {
        const stats = useMemo(() => {
            if (!patient?.studies) {
                return {
                    studiesCount: 0,
                    seriesCount: 0,
                    totalSlices: 0,
                    latestDate: null,
                };
            }

            const studiesCount = patient.studies.length;
            const seriesCount = patient.studies.reduce(
                (acc, study) => acc + (study.series?.length || 0),
                0
            );
            const totalSlices = patient.studies.reduce(
                (acc, study) =>
                    acc +
                    (study.series?.reduce(
                        (seriesAcc, series) =>
                            seriesAcc + (series.slices?.length || 0),
                        0
                    ) || 0),
                0
            );

            // Get the latest study date
            const latestDate = patient.studies
                .filter((study) => study.studyDate)
                .sort((a, b) =>
                    (b.studyDate || '').localeCompare(a.studyDate || '')
                )[0]?.studyDate;

            return { studiesCount, seriesCount, totalSlices, latestDate };
        }, [patient?.studies]);

        if (!patient) {
            return null;
        }

        return (
            <Box
                sx={{
                    p: 1.5,
                    bgcolor: 'action.hover',
                    borderRadius: 1,
                }}
            >
                {stats.latestDate && (
                    <Box display="flex" justifyContent="space-between" mb={0.5}>
                        <Typography variant="caption" color="text.secondary">
                            <FormattedMessage id="date" />
                        </Typography>
                        <Typography variant="caption" fontWeight="600">
                            <SensitiveInfoTypography>
                                {formatDateString(stats.latestDate) ||
                                    stats.latestDate}
                            </SensitiveInfoTypography>
                        </Typography>
                    </Box>
                )}
                <Box display="flex" justifyContent="space-between" mb={0.5}>
                    <Typography variant="caption" color="text.secondary">
                        <FormattedMessage id="studies" />
                    </Typography>
                    <Typography variant="caption" fontWeight="600">
                        {stats.studiesCount}
                    </Typography>
                </Box>
                <Box display="flex" justifyContent="space-between" mb={0.5}>
                    <Typography variant="caption" color="text.secondary">
                        <FormattedMessage id="series" />
                    </Typography>
                    <Typography variant="caption" fontWeight="600">
                        {stats.seriesCount}
                    </Typography>
                </Box>
                <Box display="flex" justifyContent="space-between">
                    <Typography variant="caption" color="text.secondary">
                        <FormattedMessage id="slices" />
                    </Typography>
                    <Typography variant="caption" fontWeight="600">
                        {stats.totalSlices}
                    </Typography>
                </Box>
            </Box>
        );
    }
);
PatientDetailsPanel.displayName = 'PatientDetailsPanel';

export default PatientDetailsPanel;
